// Configuración → Accesos (§1.3): quién entra al panel y medición. («Regenerar caché»
// no va acá: David lo pasó a «Actualizar» en Turnos, Ficha 4.)
// Sale del bloque «6. Seguridad, Analítica & Gestión de Administradores» de la portada.
// Guarda SOLO sus tres campos: el servidor hace merge (P-10) y exige superadmin para ellos.
import React, { useEffect, useState } from "react";
import { adminFetch } from "../api";
import { Alert, Button, Card, Field, Input, Skeleton, useToast } from "./ui";
import { ConfigPage, type CfgView } from "./ConfigPage";

type Accesos = { adminEmail: string; googleAnalyticsId: string; googleClientId: string };

const pick = (c: Partial<Accesos>): Accesos => ({
  adminEmail: (c.adminEmail || "").trim(),
  googleAnalyticsId: (c.googleAnalyticsId || "").trim(),
  googleClientId: (c.googleClientId || "").trim(),
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GA_ID = /^G-[A-Z0-9]+$/i;

const SECTION = "flex flex-col gap-[18px] px-4 py-[18px] md:p-6";
const H3 = "text-[15px] font-semibold text-adm-text";
const SUB = "text-[13px] leading-[1.55] text-adm-t55";

export function AccesosPage({
  config,
  superAdminEmail,
  loading,
  loadFailed,
  onReload,
  onNavigate,
  onSaved,
}: {
  config: Partial<Accesos>;
  superAdminEmail: string;
  /** primera carga en curso */
  loading: boolean;
  /** no llegó la configuración guardada: no se deja guardar encima */
  loadFailed: boolean;
  onReload: () => Promise<unknown>; // fetchData devuelve boolean desde la Ficha 4
  onNavigate: (view: CfgView) => void;
  /** recibe la configuración completa que devolvió el servidor */
  onSaved: (config: any) => void;
}) {
  const toast = useToast();
  const saved = pick(config);
  const [form, setForm] = useState<Accesos>(saved);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const current = pick(form);
  const dirty =
    current.adminEmail !== saved.adminEmail ||
    current.googleAnalyticsId !== saved.googleAnalyticsId ||
    current.googleClientId !== saved.googleClientId;

  // Si llega (o cambia) lo guardado y no hay nada a medio editar, se muestra eso.
  useEffect(() => {
    if (!dirty) setForm(pick(config));
  }, [config.adminEmail, config.googleAnalyticsId, config.googleClientId]);

  const emailError = current.adminEmail && !EMAIL.test(current.adminEmail) ? "No parece un correo." : null;
  const gaError = current.googleAnalyticsId && !GA_ID.test(current.googleAnalyticsId) ? "Empieza con G-, por ejemplo G-AB12CD34EF." : null;
  const blocked = loadFailed || loading;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await adminFetch("/api/frontpage/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(current),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      onSaved(data.config);
      toast.show("Accesos guardados");
    } catch (err: any) {
      const msg = err?.message || "No se pudo guardar.";
      setSaveError(msg);
      toast.show("No se guardaron los accesos", "error");
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <Button variant="primary" onClick={save} loading={saving} disabled={!dirty || blocked || !!emailError || !!gaError}>
      Guardar
    </Button>
  );

  return (
    <ConfigPage view="cfg:accesos" role="super" onNavigate={onNavigate} actions={actions}>
      {loading ? (
        <>
          <Skeleton className="h-[188px] rounded-[18px]" />
          <Skeleton className="h-[150px] rounded-[18px]" />
        </>
      ) : loadFailed ? (
        <Alert tone="red" action={<Button size="sm" onClick={() => onReload()}>Reintentar</Button>}>
          No se pudo leer la configuración guardada. Para no pisarla, no se puede guardar hasta que cargue.
        </Alert>
      ) : (
        <>
          <Card className={SECTION}>
            <div className="flex flex-col gap-0.5">
              <h3 className={H3}>Quién entra al panel</h3>
              <p className={SUB}>El superadmin ve todo. El admin de turnos ve Turnos y Farmacias.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Superadmin" htmlFor="acc-super">
                <Input id="acc-super" value={superAdminEmail} disabled />
              </Field>
              <Field
                label="Admin de turnos"
                htmlFor="acc-turnos"
                hint={emailError ? <span className="text-adm-rojo">{emailError}</span> : "Tiene que ser una cuenta de Google."}
              >
                <Input
                  id="acc-turnos"
                  type="email"
                  autoComplete="off"
                  placeholder="turnos@colegio.example"
                  value={form.adminEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, adminEmail: e.target.value })}
                />
              </Field>
            </div>
          </Card>

          <Card className={SECTION}>
            <h3 className={H3}>Medición</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Google Analytics" htmlFor="acc-ga" hint={gaError ? <span className="text-adm-rojo">{gaError}</span> : undefined}>
                <Input
                  id="acc-ga"
                  mono
                  placeholder="G-XXXXXXXXXX"
                  value={form.googleAnalyticsId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, googleAnalyticsId: e.target.value })}
                />
              </Field>
              <Field label="Google Client ID" htmlFor="acc-client">
                <Input
                  id="acc-client"
                  mono
                  placeholder="1234-abcd.apps.googleusercontent.com"
                  value={form.googleClientId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, googleClientId: e.target.value })}
                />
              </Field>
            </div>
          </Card>

          {saveError && <Alert tone="red">No se guardó: {saveError}</Alert>}
        </>
      )}
    </ConfigPage>
  );
}
